import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type ThemePreference = 'system' | 'light' | 'dark';

/** Per-browser UI conveniences only. Nothing here is sensitive; it may be lost at any time. */
export interface LocalPrefs {
  theme: ThemePreference;
  reduceTransparency: boolean;
  navExpanded: boolean;
  /** The sidebar's Telegram group is open (it also opens by itself on one of its pages). */
  telegramNavOpen: boolean;
  soundEnabled: boolean;
  /** Swap slippage cap in basis points; null lets Jupiter pick per trade. */
  swapSlippageBps: number | null;
  setTheme: (value: ThemePreference) => void;
  setReduceTransparency: (value: boolean) => void;
  setNavExpanded: (value: boolean) => void;
  setTelegramNavOpen: (value: boolean) => void;
  setSoundEnabled: (value: boolean) => void;
  setSwapSlippageBps: (value: number | null) => void;
}

/** Storage key; also read by the pre-paint script in index.html (keep them in sync). */
export const LOCAL_PREFS_KEY = 'ultrasnip-prefs';

/** localStorage that never throws (private windows, disabled storage, quota). */
const safeLocalStorage = {
  getItem: (name: string): string | null => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      /* best-effort */
    }
  },
  removeItem: (name: string): void => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      /* best-effort */
    }
  },
};

export const useLocalPrefs = create<LocalPrefs>()(
  persist(
    (setState) => ({
      theme: 'system',
      reduceTransparency: false,
      navExpanded: true,
      telegramNavOpen: true,
      soundEnabled: false,
      swapSlippageBps: null,
      setTheme: (value) => setState({ theme: value }),
      setReduceTransparency: (value) => setState({ reduceTransparency: value }),
      setNavExpanded: (value) => setState({ navExpanded: value }),
      setTelegramNavOpen: (value) => setState({ telegramNavOpen: value }),
      setSoundEnabled: (value) => setState({ soundEnabled: value }),
      setSwapSlippageBps: (value) => setState({ swapSlippageBps: value }),
    }),
    {
      name: LOCAL_PREFS_KEY,
      version: 2,
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: ({
        theme,
        reduceTransparency,
        navExpanded,
        telegramNavOpen,
        soundEnabled,
        swapSlippageBps,
      }) => ({
        theme,
        reduceTransparency,
        navExpanded,
        telegramNavOpen,
        soundEnabled,
        swapSlippageBps,
      }),
      // v1 had no theme; everything else carries over.
      migrate: (persisted) => ({ theme: 'system', ...(persisted as object) }) as LocalPrefs,
    },
  ),
);
